import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, Link } from 'react-router-dom';
import { useWebhookRegistration } from './hooks/useWebhookRegistration';
import { BILLING_REQUIRED_EVENT, type BillingRequiredDetail } from './hooks/useApi';
import WelcomePage from './pages/WelcomePage';
import UsageBanner from './components/UsageBanner';
import { AppProvider as PolarisProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';

import TemplatesPage from './pages/TemplatesPage';
import TemplatePage from './pages/TemplatePage';
import SessionsPage from './pages/SessionsPage';
import ActivityPage from './pages/ActivityPage';
import SessionDetailPage from './pages/SessionDetailPage';
import TemplateEditorPage from './pages/TemplateEditorPage';
import OrderManualSignPage from './pages/OrderManualSignPage';
import SettingsPage from './pages/SettingsPage';
import SigningPage from './pages/SigningPage';
import VerifyPage from './pages/VerifyPage';
import AdminApp from './pages/admin/AdminApp';

// App Bridge 4 types (including ui-nav-menu) are declared in src/shopify.d.ts.

// ---------------------------------------------------------------------------
// Merchant dashboard (embedded in Shopify — needs Polaris + App Bridge 4 CDN)
// ---------------------------------------------------------------------------

/** Very subtle SignShield badge — bottom-right corner of the embedded app. */
function BrandBadge() {
  return (
    <a
      href="https://merchantkits.com/signshield"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        position: 'fixed',
        bottom: '12px',
        right: '16px',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        textDecoration: 'none',
        opacity: 0.7,
        transition: 'opacity 0.2s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
      title="Powered by SignShield"
    >
      <img src={(import.meta.env.VITE_APP_URL || window.location.origin) + '/logo-icon.png'} alt="SignShield" style={{ width: '20px', height: '20px' }} />
      <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500, letterSpacing: '0.02em' }}>
        SignShield
      </span>
    </a>
  );
}

function MerchantRoutes() {
  useWebhookRegistration();

  // Set when any API call answers 402 — no active subscription yet.
  const [billingRequired, setBillingRequired] = useState<BillingRequiredDetail | null>(null);
  useEffect(() => {
    const onRequired = (e: Event) => setBillingRequired((e as CustomEvent<BillingRequiredDetail>).detail);
    window.addEventListener(BILLING_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(BILLING_REQUIRED_EVENT, onRequired);
  }, []);

  if (billingRequired) {
    return (
      <>
        <BrandBadge />
        <WelcomePage
          subscriptionStatus={billingRequired.subscriptionStatus}
          trialDaysRemaining={billingRequired.trialDaysRemaining}
        />
      </>
    );
  }

  return (
    <>
      <BrandBadge />
      <UsageBanner />
      {/* App Bridge 4 navigation — renders links in Shopify admin's left sidebar */}
      <ui-nav-menu>
        <a href="/" rel="home">Templates</a>
        <a href="/sessions">Signing Sessions</a>
        <a href="/activity">Activity</a>
        <a href="/settings">Settings</a>
      </ui-nav-menu>
      <Routes>
        <Route path="/" element={<TemplatesPage />} />
        <Route path="/templates/new" element={<TemplatePage />} />
        <Route path="/templates/:id/edit" element={<TemplatePage />} />
        <Route path="/templates/editor/new" element={<TemplateEditorPage />} />
        <Route path="/templates/editor/:id/edit" element={<TemplateEditorPage />} />
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="/sessions/:id" element={<SessionDetailPage />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/orders/:orderId/manual" element={<OrderManualSignPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes>
    </>
  );
}

function MerchantApp() {
  // App Bridge 4 CDN auto-initializes from <meta name="shopify-api-key"> and
  // the ?host= URL param — no AppBridgeProvider wrapper required.
  return (
    <PolarisProvider i18n={enTranslations} linkComponent={AdapterLink}>
      <BrowserRouter>
        <MerchantRoutes />
      </BrowserRouter>
    </PolarisProvider>
  );
}

// ---------------------------------------------------------------------------
// Public pages — signing + verification (no App Bridge, no Polaris)
// ---------------------------------------------------------------------------

function PublicApp() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/sign/:token" element={<SigningPage />} />
        <Route path="/sign/:token/signed" element={<SigningPage />} />
        <Route path="/verify" element={<VerifyPage />} />
      </Routes>
    </BrowserRouter>
  );
}

// ---------------------------------------------------------------------------
// Root — branch on URL path before any provider mounts
// ---------------------------------------------------------------------------

/**
 * Shopify always loads the merchant dashboard inside the admin iframe (or with
 * shop/host params on the way in). Anything else is someone opening the app
 * URL directly — they get the owner dashboard login instead.
 */
function isShopifyContext(): boolean {
  const params = new URLSearchParams(window.location.search);
  return window.top !== window.self || params.has('shop') || params.has('host');
}

export default function App() {
  const path = window.location.pathname;
  const isPublicRoute =
    path.startsWith('/sign/') || path === '/verify' || path.startsWith('/verify/');
  if (isPublicRoute) return <PublicApp />;
  return isShopifyContext() ? <MerchantApp /> : <AdminApp />;
}

// ---------------------------------------------------------------------------
// Polaris link adapter (merchant dashboard only)
// ---------------------------------------------------------------------------

function AdapterLink({
  url,
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { url: string }) {
  const isExternal =
    url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//');
  if (isExternal) {
    return (
      <a href={url} {...rest} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <Link to={url} {...rest}>
      {children}
    </Link>
  );
}
